import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html } from 'lit';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { LangQuoteItemCommentModal } from '../../../localization/scripts/quotes/quote-item-comment-modal-lang';
import { when } from 'lit/directives/when.js';

type ModalResult = 'update' | 'cancel';

export interface QuoteItemCommentModalResult {
  result: ModalResult;
  comment?: string | null;
}

export async function getCommentEditModal(
  modalSize = 'modal-xl',
  modalTitle = 'Comment',
  readonly: boolean = false,
  comment?: string | null
): Promise<QuoteItemCommentModalResult> {
  const modal = new QuoteItemCommentModal(readonly, modalSize, modalTitle, comment);
  await modal.showModal();

  return {
    result: modal.result,
    comment: modal.comment
  };
}

class QuoteItemCommentModal extends LitBaseModal {
  comment?: string | null = null;
  result: ModalResult = 'cancel';

  protected _modalTitle: string;
  protected elementId = newGuid();
  private _readonly: boolean = false;
  private _dataBinding: DataBinding;
  private _dataTracker: DataTracker;
  private _forms: FormInputAssistant;

  constructor(
    readonly = false,
    modalSize = 'modal-xl',
    modalTitle = LangQuoteItemCommentModal.commentTitle(),
    comment?: string | null
  ) {
    super();

    this.comment = comment;
    this._modalTitle = modalTitle;

    this._dataBinding = new DataBinding(this.ui, this.elementId, input => `${input}-${this.elementId}`);

    this._dataTracker = new DataTracker(this._dataBinding);
    this._dataTracker.addObjectBinding(() => this, 'comment', 'comment', FieldType.string, true);

    this._forms = new FormInputAssistant(this._dataTracker);
    this._readonly = readonly;
    this.modal.events.modalSize = modalSize;
  }

  override bodyTemplate() {
    return html`
      <form class="modal-confirmation-reason form-one-col">
        <div class="row">
          <div class="form-column">
            ${when(
              this._readonly,
              () => html`${this._forms.noteReadonly('comment', undefined, 15)}`,
              () => html`${this._forms.note('comment', undefined, 5000, undefined, undefined, 15)}`
            )}
          </div>
        </div>
      </form>
    `;
  }

  protected prepareForSave() {
    this._dataTracker.applyChangeToValue();
  }

  title() {
    return this._modalTitle;
  }

  override footerTemplate() {
    const cancelEvent = () => {
      this.result = 'cancel';
      this.comment = null;
      this.hideModal();
    };

    const updateEvent = () => {
      this.prepareForSave();
      this.result = 'update';
      this.hideModal();
    };

    const updateButton = () => {
      if (this._readonly) return html``;
      return this._forms.buttonPrimary(LangQuoteItemCommentModal.updateButton(), {
        events: { click: updateEvent },
        readonly: this._readonly
      });
    };

    return html`
      ${this._forms.buttonDefault(LangQuoteItemCommentModal.closeButton(), { events: { click: cancelEvent } })}
      ${updateButton()}
    `;
  }
}
